/**
 * Le geste délibéré sur une case de pointage (`lib/garderie/appui.ts`).
 *
 * `suivi` se déplie sur le bouton (`{...suivi}`) : il note le contact, suit le
 * doigt jusqu'au relâchement (Chrome place le clic tactile au point de départ :
 * l'écart se mesure pendant le contact) et voit quand le navigateur reprend le
 * geste pour faire défiler. `geste(cle)` se demande dans `onClick` : vrai si
 * le clic vient d'un appui franc et n'est pas le rebond d'un geste précédent sur
 * la même case. Un défilement de n'importe quel élément de la page (la liste, la
 * grille qui défile de côté, la page) est noté au passage, en capture.
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { PointerEvent as EvenementPointeur } from 'react';
import { aDefile, estUnAppuiFranc, estUnRebond } from '../../lib/garderie/appui';

interface Contact {
  x: number;
  y: number;
  t: number;
  tactile: boolean;
  ecart: number;
  fin: number | null;
  annule: boolean;
}

export function useAppuiFranc() {
  const contact = useRef<Contact | null>(null);
  const dernierDefilement = useRef(Number.NEGATIVE_INFINITY);
  const derniersGestes = useRef(new Map<string, number>());

  useEffect(() => {
    const noter = () => {
      dernierDefilement.current = performance.now();
    };
    document.addEventListener('scroll', noter, { capture: true, passive: true });
    return () => document.removeEventListener('scroll', noter, { capture: true });
  }, []);

  const suivi = useMemo(() => {
    const ecarter = (e: EvenementPointeur) => {
      const c = contact.current;
      if (c) c.ecart = Math.max(c.ecart, Math.hypot(e.clientX - c.x, e.clientY - c.y));
    };
    return {
      onPointerDown: (e: EvenementPointeur) => {
        contact.current = {
          x: e.clientX,
          y: e.clientY,
          t: performance.now(),
          tactile: e.pointerType === 'touch' || e.pointerType === 'pen',
          ecart: 0,
          fin: null,
          annule: false,
        };
      },
      onPointerMove: ecarter,
      onPointerUp: (e: EvenementPointeur) => {
        ecarter(e);
        if (contact.current) contact.current.fin = performance.now();
      },
      onPointerCancel: () => {
        if (contact.current) contact.current.annule = true;
      },
    };
  }, []);

  const geste = useCallback((cle: string): boolean => {
    const c = contact.current;
    contact.current = null;
    const maintenant = performance.now();
    /* Un clic sans contact connu : le clavier (Entrée, Espace), ou un lecteur d'écran. */
    if (c) {
      const franc = estUnAppuiFranc({
        tactile: c.tactile,
        deplacement: c.ecart,
        duree: (c.fin ?? maintenant) - c.t,
        defilement: aDefile(dernierDefilement.current, c.t),
        annule: c.annule,
      });
      if (!franc) return false;
    }
    if (estUnRebond(derniersGestes.current.get(cle), maintenant)) return false;
    derniersGestes.current.set(cle, maintenant);
    return true;
  }, []);

  return { suivi, geste };
}
